import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import {
  BarChart, Card, LineChart, SectionHead,
} from '../../PipelineComponents';
import { formattedCount, funnelStage, percentOf } from '../../../pipelineHelpers';
import MailOrderMembersCard from './MailOrderMembersCard';

const beforeAfter = (value, unit = '') => `${value || '0.0'}${unit}`;

const FUNNEL_TIPS = {
  accepted: {
    text: 'Members who took up a 90-day mail order.',
    steps: ['Counted from the conversion fills recorded against the member.'],
    note: 'This sits at the top of the funnel while eligible sits below it, which is back to front - you'
      + ' cannot accept before you are eligible. The steps need reordering before the funnel reads correctly.',
    warn: true,
  },
  eligible: {
    text: 'Members whose plan and medication allow a 90-day mail order.',
    steps: ['Checked against the plan and the medication on file.'],
    note: 'It should be the widest step in the funnel. Anything smaller than the step above it means the two'
      + ' are counting different populations and the percentages cannot be quoted.',
    warn: true,
  },
  offered: {
    text: 'Members who were actually offered a longer supply.',
    steps: ['Counted once per member, not once per offer.'],
    note: 'If it matches accepted exactly, the offer is not being recorded separately from the outcome - a'
      + ' 100% acceptance rate almost always means one event is standing in for both.',
    warn: true,
  },
  filled: {
    text: 'Members who went on to fill a 90-day supply.',
    steps: ['The only step confirmed by a dispensing record.'],
    note: 'The funnel is only useful once each step is captured as its own event. Where several steps show'
      + ' the same number, this is the one to trust.',
  },
};

const MONTH_TIP = {
  text: 'Members converting to a 90-day supply in this month.',
  steps: [
    'Each member appears once, in the month they first converted.',
    'Later 90-day fills do not count again.',
  ],
  note: 'The current month is still running, so the last bar always understates itself. Three months in the'
    + ' same range is the useful signal, not any single month.',
};

const MailOrder = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getMailOrderConversionsSummary(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const {
    stages, months, worth, worthMembers,
  } = useMemo(() => {
    const funnel = summary?.funnel;
    const conversionWorth = summary?.conversionWorth;

    const accepted = +(funnel?.totalConversionFills || 0);
    const eligible = +(funnel?.eligible || 0);
    const converted = +(funnel?.converted || 0);
    const filledAt90 = +(funnel?.filledAt90 || 0);

    const max = _.max([accepted, eligible, converted, filledAt90]);

    return {
      stages: funnel
        ? [
          funnelStage('Accepted', accepted, percentOf(accepted, max), FUNNEL_TIPS.accepted),
          funnelStage('Eligible for 90-day mail order', eligible, percentOf(eligible, max), FUNNEL_TIPS.eligible),
          funnelStage('Offered the conversion', converted, percentOf(converted, max), FUNNEL_TIPS.offered),
          funnelStage('Filled at 90 days', filledAt90, percentOf(funnel.filledAt90, max), FUNNEL_TIPS.filled),
        ]
        : [],

      months: (summary?.byMonth || []).map((d) => ({
        label: moment(d.month).format('MMM YYYY'),
        value: +(d.conversions || 0),
        tooltip: MONTH_TIP,
      })),

      worthMembers: conversionWorth?.convertedMembers,

      worth: conversionWorth
        ? [
          {
            label: 'PDC',
            before: beforeAfter(conversionWorth.pdcBefore, '%'),
            after: beforeAfter(conversionWorth.pdcAfter, '%'),
          },
          {
            label: 'Avg cumulative gap days',
            before: beforeAfter(conversionWorth.avgGapBefore),
            after: beforeAfter(conversionWorth.avgGapAfter),
          },
          {
            label: 'Refill on time',
            before: beforeAfter(conversionWorth.onTimeRefillBefore, '%'),
            after: beforeAfter(conversionWorth.onTimeRefillAfter, '%'),
          },
          {
            label: 'Fills per member per year',
            before: beforeAfter(conversionWorth.fillsPerYearBefore),
            after: beforeAfter(conversionWorth.fillsPerYearAfter),
          },
          {
            label: '6-month retention',
            after: beforeAfter(conversionWorth.retentionAfter6m, '%'),
          },
        ]
        : [],
    };
  }, [summary]);

  return (
    <>
      <SectionHead section={section} />

      <div className={`${classes.row} ${classes.row_3}`}>
        <Card
          title="Conversion funnel"
          caption="Members eligible for a 90-day mail order"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!stages.length}
        >
          <BarChart data={stages} />
        </Card>

        <Card
          title="Conversions by month"
          caption="Members newly filling at 90 days"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!months.length}
        >
          <LineChart data={months} />
        </Card>

        <Card
          title="What conversion is worth"
          caption={`${formattedCount(worthMembers)} converted members · 90 days before vs. after`}
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!worth.length}
        >
          <div className={`${classes.kv_list} ${classes.bordered}`}>
            {worth.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.label}

                <span className={classes.kv_value}>
                  {!!item.before && (
                    <>
                      {item.before}
                      {' → '}
                    </>
                  )}

                  <b>{item.after}</b>
                </span>
              </div>
            ))}
          </div>

          <p className={classes.note}>
            <span className={`${classes.badge} ${classes.badge_new}`}>needs a flag</span>
            {' '}
            Requires source-of-fill tracking, or a conversion flag on the script, so a member's first
            90-day fill after a retail/30-day history is recognised as a conversion rather than a normal
            refill.
          </p>
        </Card>
      </div>

      <MailOrderMembersCard params={params} />
    </>
  );
};

MailOrder.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default MailOrder;
